/**
 * Small primitives, matching the lonefox workspace.
 */

import type { ReactNode } from 'react'

import { statusLabel } from '../subscription.js'
import type { Subscription } from '../api.js'

export function Card({
  title,
  note,
  action,
  children,
}: {
  title?: string | undefined
  note?: string | undefined
  action?: ReactNode | undefined
  children: ReactNode
}) {
  return (
    <section className="card">
      {(title || action) && (
        <div className="card__head">
          {title ? <h3>{title}</h3> : <span />}
          {action}
        </div>
      )}
      {note ? <p className="card__note">{note}</p> : null}
      {children}
    </section>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string | undefined
  children: ReactNode
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  )
}

export function Alert({
  tone = 'error',
  children,
}: {
  tone?: 'error' | 'warn' | 'ok'
  children: ReactNode
}) {
  return (
    // `status` for good news, `alert` for bad: a screen reader interrupts for
    // an alert, and interrupting someone to tell them a save worked is rude.
    <p className={`alert alert--${tone}`} role={tone === 'ok' ? 'status' : 'alert'}>
      {children}
    </p>
  )
}

export function Pill({
  tone,
  children,
}: {
  tone: 'ok' | 'warn' | 'error' | 'muted'
  children: ReactNode
}) {
  return <span className={`pill pill--${tone}`}>{children}</span>
}

/** A definition row, so labels and values line up across every card. */
export function Rows({ children }: { children: ReactNode }) {
  return <div className="rows">{children}</div>
}

export function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <span className="k">{label}</span>
      <span className="v">{children}</span>
    </div>
  )
}

/** Two letters from a name or email, for the rail avatar. */
export function initials(source: string): string {
  const name = source.includes('@') ? (source.split('@')[0] ?? source) : source
  const parts = name.split(/[\s._-]+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase()
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
}

/**
 * A confirmation that has room to say what will happen.
 *
 * `window.confirm` was doing this job and could not do it well: it renders a
 * wall of unformatted text, cannot emphasise the sentence that matters, and on
 * some browsers is suppressed entirely — which would turn "release this
 * device" into a one-click irreversible action. §7.4 asks the release
 * confirmation to state what release does *and does not* do, and that is more
 * than one line.
 */
export function Confirm({
  title,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
  children,
}: {
  title: string
  confirmLabel: string
  busy?: boolean
  onConfirm: () => void
  onCancel: () => void
  children: ReactNode
}) {
  return (
    <div
      className="scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      // Clicking the backdrop cancels; clicking the dialog must not. Without
      // the second check, every click inside the box closes it.
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel()
      }}
    >
      <div className="dialog">
        <h3>{title}</h3>
        <div className="dialog__body">{children}</div>
        <div className="actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="danger" onClick={onConfirm} disabled={busy}>
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * What a subscription state blocks, and what it does not.
 *
 * Shared between the subscription screen and the overview so the two cannot
 * tell an owner different things about the same state — which is the failure
 * §7.3 and §16.16 are most exposed to, because "locked" is a frightening word
 * and the reassuring half is the part that gets dropped.
 */
export function Consequences({
  blocked,
  working,
}: {
  blocked: string[]
  working: string[]
}) {
  return (
    <div className="consequences">
      {blocked.length > 0 ? (
        <div>
          <span className="consequences__head consequences__head--blocked">Blocked right now</span>
          <ul>
            {blocked.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <div>
        <span className="consequences__head consequences__head--working">Still working</span>
        <ul>
          {working.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}

/** The subscription status, rendered from the one place that decides it. */
export function StatusPill({ subscription }: { subscription: Subscription }) {
  const label = statusLabel(subscription)
  return <Pill tone={label.tone}>{label.text}</Pill>
}
